import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import apiService from '../../services/apiService';

// Custom hook for debouncing
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const emptyCustomer = {
  customerId: undefined,
  bookPage: '',
  customerName: '',
  customerNameTamil: '',
  customerCity: '',
  customerMobile: '',
  customerState: '',
  customerOfficeNumber: '',
  shopName: '',
  customerEmail: '',
  customerPostalCode: ''
};

const AddCustomerDialog = ({ open, onClose, onSaveSuccess, initialData = null }) => {
  const [form, setForm] = useState(emptyCustomer);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [mobileError, setMobileError] = useState('');
  const [saveError, setSaveError] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const debouncedMobile = useDebounce(form.customerMobile, 500);

  useEffect(() => {
    if (open) {
      if (initialData) {
        setForm({ ...emptyCustomer, ...initialData });
      } else {
        setForm(emptyCustomer);
      }
      setSubmitAttempted(false);
      setMobileError('');
      setSaveError('');
    }
  }, [open, initialData]);

  useEffect(() => {
    const checkMobile = async () => {
      if (debouncedMobile) {
        if (debouncedMobile.length > 10) {
          setMobileError('Mobile number cannot exceed 10 digits.');
        } else if (debouncedMobile.length === 10) {
          // Don't validate if we are editing and the mobile number hasn't changed
          if (form.customerId && initialData && debouncedMobile === initialData.customerMobile) {
            setMobileError('');
            return;
          }
          try {
            const response = await apiService.customers.checkMobile(debouncedMobile);
            if (response.data.exists) {
              setMobileError('Mobile number already exists.');
            } else {
              setMobileError('');
            }
          } catch (error) {
            console.error('Error checking mobile number:', error);
            setMobileError(''); // Assume it's fine if API fails
          }
        } else {
          setMobileError('Mobile number must be 10 digits.');
        }
      } else {
        setMobileError('');
      }
    };
    checkMobile();
  }, [debouncedMobile, form.customerId, initialData]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setSaveError('');
    if (name === 'customerMobile') {
      if (value.length > 10) {
        setMobileError('Mobile number cannot exceed 10 digits.');
      } else {
        setMobileError('');
      }
    }
  };

  const transliterateToTamil = (text) => {
    if (!text) return '';

    const vowels = {
      'a': 'அ', 'aa': 'ஆ', 'i': 'இ', 'ii': 'ஈ', 'u': 'உ', 'uu': 'ஊ',
      'e': 'எ', 'ee': 'ஏ', 'ai': 'ஐ', 'o': 'ஒ', 'oo': 'ஓ', 'au': 'ஔ'
    };

    const signs = {
      'a': '', 'aa': 'ா', 'i': 'ி', 'ii': 'ீ', 'u': 'ு', 'uu': 'ூ',
      'e': 'ெ', 'ee': 'ே', 'ai': 'ை', 'o': 'ொ', 'oo': 'ோ', 'au': 'ௌ'
    };

    const consonants = {
      'k': 'க்', 'g': 'க்', 'ng': 'ங்', 'c': 'ச்', 'ch': 'ச்', 'j': 'ஜ்', 'gn': 'ஞ்', 'nj': 'ஞ்',
      't': 'ட்', 'd': 'ட்', 'N': 'ண்', 'th': 'த்', 'dh': 'த்', 'n': 'ந்',
      'p': 'ப்', 'b': 'ப்', 'm': 'ம்',
      'y': 'ய்', 'r': 'ர்', 'l': 'ல்', 'v': 'வ்', 'z': 'ழ்', 'zh': 'ழ்', 'L': 'ள்', 'R': 'ற்',
      'sh': 'ஷ்', 'S': 'ஸ்', 's': 'ஸ்', 'h': 'ஹ்', 'ksh': 'க்ஷ்'
    };

    const overrides = {
      'n': 'ன்', 'na': 'ந', 'naa': 'நா', 'ni': 'நி', 'nii': 'நீ', 'nu': 'நு', 'nuu': 'நூ', 'ne': 'நெ', 'nee': 'நே', 'nai': 'நை', 'no': 'நொ', 'noo': 'நோ', 'nau': 'நௌ',
      'sa': 'ச', 'saa': 'சா', 'si': 'சி', 'sii': 'சீ', 'su': 'சு', 'suu': 'சூ', 'se': 'செ', 'see': 'சே', 'sai': 'சை', 'so': 'சொ', 'soo': 'சோ', 'sau': 'சௌ'
    };

    let result = '';
    let i = 0;
    const len = text.length;
    const consKeys = Object.keys(consonants).sort((a, b) => b.length - a.length);
    const vowKeys = Object.keys(vowels).sort((a, b) => b.length - a.length);

    while (i < len) {
      let match = false;
      const sub = text.substring(i).toLowerCase();

      // 1. Consonant + Vowel or Consonant
      for (const c of consKeys) {
        if (sub.startsWith(c)) {
          const subAfter = sub.substring(c.length);
          let vMatch = null;
          for (const v of vowKeys) {
            if (subAfter.startsWith(v)) {
              vMatch = v;
              break;
            }
          }

          if (vMatch) {
            const key = c + vMatch;
            if (overrides[key]) {
              result += overrides[key];
            } else {
              const base = consonants[c].replace('்', '');
              result += base + signs[vMatch];
            }
            i += c.length + vMatch.length;
          } else {
            // Pure consonant
            if (overrides[c]) {
              result += overrides[c];
            } else {
              result += consonants[c];
            }
            i += c.length;
          }
          match = true;
          break;
        }
      }

      if (match) continue;

      // 2. Vowel (Independent)
      for (const v of vowKeys) {
        if (sub.startsWith(v)) {
          result += vowels[v];
          i += v.length;
          match = true;
          break;
        }
      }

      if (match) continue;

      // 3. Fallback
      result += text[i];
      i++;
    }
    return result;
  };

  const saveCustomer = async () => {
    setSaveError('');
    try {
      setSubmitAttempted(true);
      const nameOk = (form.customerName || '').trim().length > 0;
      const mobileVal = (form.customerMobile || '').trim();
      const mobileOk = mobileVal.length > 0;

      if (!nameOk || !mobileOk || mobileError) {
        return;
      }

      if (mobileVal.length !== 10) {
        setMobileError('Mobile number must be 10 digits.');
        return;
      }

      const payload = { ...form };
      let response;

      if (payload.customerId) {
        response = await apiService.customers.update(payload.customerId, payload);
      } else {
        response = await apiService.customers.create(payload);
      }

      if (onSaveSuccess) {
        onSaveSuccess(response.data);
      }
      onClose();
    } catch (e) {
      console.error('Save failed', e);
      const resData = e.response?.data;
      const errorMessage = resData?.message || resData?.error || e.message || 'Failed to save customer';
      if (errorMessage.toLowerCase().includes('mobile number')) {
        setMobileError(errorMessage);
      } else {
        setSaveError(errorMessage);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" TransitionComponent={Transition}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {form.customerId ? 'Edit Customer' : 'Add Customer'}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 2 }}>
          <TextField label="Page" name="bookPage" value={form.bookPage} onChange={handleFormChange} size="small" />
          <TextField
            label="Name"
            name="customerName"
            value={form.customerName}
            onChange={handleFormChange}
            required
            error={submitAttempted && !(form.customerName || '').trim()}
            helperText={submitAttempted && !(form.customerName || '').trim() ? 'Required' : ''}
            size="small"
          />
          <TextField
            label="Name (Tamil)"
            name="customerNameTamil"
            value={form.customerNameTamil}
            onChange={handleFormChange}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                if (e.key === ' ') {
                  e.preventDefault();
                  const tn = transliterateToTamil(form.customerNameTamil || '');
                  setForm(prev => ({ ...prev, customerNameTamil: tn + ' ' }));
                } else {
                  const tn = transliterateToTamil(form.customerNameTamil || '');
                  setForm(prev => ({ ...prev, customerNameTamil: tn }));
                }
              }
            }}
            size="small"
          />
          <TextField label="City" name="customerCity" value={form.customerCity} onChange={handleFormChange} size="small" />
          <TextField
            label="Mobile"
            name="customerMobile"
            value={form.customerMobile}
            onChange={handleFormChange}
            required
            error={!!mobileError || (submitAttempted && !(form.customerMobile || '').trim())}
            helperText={mobileError || (submitAttempted && !(form.customerMobile || '').trim() ? 'Required' : '')}
            size="small"
          />
          <TextField label="State" name="customerState" value={form.customerState} onChange={handleFormChange} size="small" />
          <TextField label="Office No" name="customerOfficeNumber" value={form.customerOfficeNumber} onChange={handleFormChange} size="small" />
          <TextField label="Shop" name="shopName" value={form.shopName} onChange={handleFormChange} size="small" />
          <TextField label="Email" name="customerEmail" value={form.customerEmail} onChange={handleFormChange} size="small" />
          <TextField label="Postal Code" name="customerPostalCode" value={form.customerPostalCode} onChange={handleFormChange} size="small" />
        </Box>
        {saveError && (
          <Box sx={{ mt: 2, p: 1, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ffcdd2' }}>
            <Typography color="error" variant="body2" align="center">
              {saveError}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={saveCustomer} disabled={!!mobileError}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCustomerDialog;
